import background from "@assets/img/auth/customer/background.png";
import logowhite from "@assets/img/logo/logo-simple.png";
import { Button } from "@components/buttons/button";
import { Input } from "@components/forms/input";
import { InputGroup } from "@components/forms/input-group";
import { ModalRegister } from "@components/modals/modal-register";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAuthContext } from "@hooks/contexts/auth-context";
import { useModalContext } from "@hooks/contexts/modal-context";
import { useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  FaArrowLeft,
  FaArrowRight,
  FaChartLine,
  FaLock,
  FaShieldAlt,
  FaTicketAlt,
  FaUserPlus,
  FaUsers,
} from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import * as yup from "yup";

interface IFormValues {
  userName: string;
  password: string;
  termsCheck: boolean;
}

const featureCards = [
  {
    icon: FaTicketAlt,
    title: "Organização",
    text: "Gerencie vendas de forma simples.",
    color: "primary",
  },
  {
    icon: FaChartLine,
    title: "Transparência",
    text: "Acompanhe ganhos e campanhas.",
    color: "success",
  },
  {
    icon: FaShieldAlt,
    title: "Acesso Seguro",
    text: "Dados protegidos na plataforma.",
    color: "warning",
  },
  {
    icon: FaUsers,
    title: "Parceiros Ativos",
    text: "Controle afiliados e comissões.",
    color: "info",
  },
];

export function Auth() {
  const navigate = useNavigate();
  const location = useLocation();
  const { changeHash } = useModalContext();
  const { signIn, customer } = useAuthContext();

  const scheme = yup.object().shape({
    userName: yup.string().required("E-mail é obrigatório"),
    password: yup.string().required("Senha é obrigatório"),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<IFormValues>({
    resolver: yupResolver(scheme) as any,
    mode: "onSubmit",
    reValidateMode: "onBlur",
  });

  const handleLogin = useCallback(
    async (data: IFormValues) => {
      await signIn(data);
    },
    [signIn],
  );

  function handleRegister() {
    changeHash("#register");
  }

  const handleKeyPress = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Enter") {
        const activeElement = document.activeElement;
        if (
          activeElement instanceof HTMLInputElement ||
          activeElement instanceof HTMLTextAreaElement
        ) {
          activeElement.blur();
        }
        event.preventDefault();
        handleSubmit(handleLogin)();
      }
    },
    [handleLogin, handleSubmit],
  );

  useEffect(() => {
    if (customer) {
      navigate("/dashboard", { replace: true });
    }
  }, [customer, navigate]);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyPress);
    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [handleKeyPress]);

  return (
    <>
      <main
        className="auth-page container-fluid position-relative overflow-hidden px-0 text-white"
        style={{
          minHeight: "100vh",
          background:
            "radial-gradient(circle at 58% 12%, rgba(13,110,253,.28), transparent 28%), radial-gradient(circle at 92% 68%, rgba(25,135,84,.16), transparent 26%), linear-gradient(120deg, #061323 0%, #071827 46%, #02060b 100%)",
        }}
      >
        <div
          className="position-absolute top-0 start-0 w-100 h-100 d-none d-lg-block"
          style={{
            backgroundImage: `url(${background})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "60% auto",
            backgroundPosition: "47% 12%",
            opacity: 0.18,
            filter: "saturate(.8)",
          }}
        />
        <div
          className="position-absolute top-0 start-0 w-100 h-100"
          style={{
            background:
              "linear-gradient(90deg, rgba(2,8,14,.24), rgba(2,8,14,.08) 45%, rgba(2,8,14,.34))",
          }}
        />

        <header
          className="auth-page-header position-relative p-3 p-lg-4"
          style={{ zIndex: 3 }}
        >
          <div className="d-flex justify-content-between align-items-center gap-3">
            <button
              type="button"
              className="btn btn-outline-light rounded-4 px-3 px-sm-4 py-2 fw-semibold"
              onClick={() => navigate("/")}
              title="Voltar para a página inicial."
            >
              <FaArrowLeft className="me-2" />
              <span className="d-none d-sm-inline">Voltar para o site</span>
              <span className="d-sm-none">Voltar</span>
            </button>

            <button
              type="button"
              className="btn btn-primary rounded-4 px-3 px-sm-4 py-2 fw-bold text-white shadow-sm"
              onClick={handleRegister}
              title="Criar cadastro na plataforma."
            >
              <FaUserPlus className="me-2" />
              Cadastre-se
            </button>
          </div>
        </header>

        <div
          className="auth-page-content container position-relative"
          style={{ zIndex: 2 }}
        >
          <div
            className="row align-items-center g-5 py-4 py-lg-5"
            style={{ minHeight: "calc(100vh - 96px)" }}
          >
            <section className="auth-page-hero col-lg-7">
              <div className="mb-4">
                <img
                  src={logowhite}
                  className="auth-page-logo img-fluid mb-4"
                  style={{ maxHeight: 180, objectFit: "contain" }}
                  alt="Logo"
                />
                <h1
                  className="display-5 fw-bold text-white mb-3"
                  style={{ lineHeight: 1.08 }}
                >
                  Acompanhe campanhas, parceiros e resultados{" "}
                  <span className="text-info">com segurança.</span>
                </h1>
                <p className="fs-5 text-white-50 mb-0">
                  Controle suas vendas, comissões, tickets e movimentações
                  financeiras com clareza para tomar decisões melhores.
                </p>
              </div>

              <div className="auth-page-features row g-3">
                {featureCards.map(({ icon: Icon, title, text, color }) => (
                  <div className="col-6 col-sm-6 col-xl-5" key={title}>
                    <div
                      className="auth-feature-card border border-light border-opacity-25 rounded-4 p-3 h-100"
                      style={{ background: "rgba(255,255,255,.035)" }}
                    >
                      <div
                        className={`bg-${color} text-white rounded-3 align-items-center p-2 mb-2`}
                      >
                        <Icon className="fs-5 me-2" />
                        <span className="small fw-semibold text-white mb-1">
                          {title}
                        </span>
                      </div>
                      <small className="text-white-50">{text}</small>
                    </div>
                  </div>
                ))}
              </div>
            </section>
            <section className="col-lg-5">
              <div
                className="rounded-4 border p-4 p-sm-5 shadow-lg ms-lg-auto"
                style={{
                  maxWidth: 470,
                  background: "rgba(5, 14, 26, .9)",
                  borderColor: "rgba(58, 153, 255, .42)",
                  backdropFilter: "blur(18px)",
                }}
              >
                <div className="mb-4">
                  <h2 className="fw-bold text-white mb-2">
                    Bem-vindo de volta!
                  </h2>
                  <p className="text-white-50 mb-0">
                    Acesse sua conta e continue no controle das suas campanhas.
                  </p>
                </div>

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit(handleLogin)();
                  }}
                >
                  <div className="row g-3">
                    <div className="col-12">
                      <Input
                        id="userName"
                        name="userName"
                        label="Usuário ou e-mail"
                        labelColor="muted"
                        placeholder="Seu usuário ou e-mail"
                        type="text"
                        register={register("userName")}
                        errorMessage={errors?.userName?.message}
                      />
                    </div>

                    <div className="col-12">
                      <InputGroup
                        id="password"
                        name="password"
                        label="Senha"
                        labelColor="muted"
                        placeholder="Sua senha"
                        type="password"
                        register={register("password")}
                        errorMessage={errors?.password?.message}
                      />
                    </div>

                    <div className="col-12 d-grid gap-3 mt-2">
                      <Button className="btn btn-primary w-100 py-3 rounded-4 text-white fw-bold">
                        Entrar
                        <FaArrowRight className="ms-2" />
                      </Button>

                      <div className="d-flex align-items-center gap-3 text-white-50">
                        <div className="border-top border-light border-opacity-25 flex-fill" />
                        <span>ou</span>
                        <div className="border-top border-light border-opacity-25 flex-fill" />
                      </div>

                      <button
                        type="button"
                        className="btn btn-outline-light w-100 py-3 rounded-4 fw-semibold"
                        onClick={handleRegister}
                      >
                        <FaUserPlus className="me-2" />
                        Criar conta
                      </button>
                    </div>
                  </div>
                </form>

                <div className="d-flex align-items-center gap-3 mt-4 text-success">
                  <FaLock />
                  <small>
                    <span className="fw-bold d-block">Ambiente Protegido</span>
                    <span className="text-white-50">
                      Seus dados estão seguros conosco.
                    </span>
                  </small>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>

      <ModalRegister
        isOpen={location.hash === "#register"}
        onClose={() => changeHash("")}
      />
    </>
  );
}

